import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  ExpandMore as ExpandMoreIcon,
  ShowChart as ShowChartIcon,
  AccountBalance as AccountBalanceIcon,
  LocalAtm as LocalAtmIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { macroForecastAPI } from '../services/api';
import MacroOutlookSummary from '../components/forecast/MacroOutlookSummary';
import SimpleForecastCard from '../components/forecast/SimpleForecastCard';
import { MacroForecastSkeleton } from '../components/forecast';

const DashboardMacroForecast = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [generating, setGenerating] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [expandedTargets, setExpandedTargets] = useState({});
  const [selectedHorizon, setSelectedHorizon] = useState('3M'); // Default to 3M

  // Use React Query without auto-refresh
  const {
    data: dashboardData,
    isLoading: loading,
    isRefetching: refreshing,
    error,
    refetch
  } = useQuery({
    queryKey: ['macroForecastDashboard'],
    queryFn: async () => {
      const response = await macroForecastAPI.getDashboard();
      return response.data;
    },
    refetchOnWindowFocus: false, // Don't refetch when user returns to tab
    refetchOnMount: true, // Only refetch on mount
    staleTime: Infinity, // Data never becomes stale automatically
    retry: 2,
    retryDelay: 1000
  });

  // Mutation for generating forecasts
  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!dashboardData?.targets) {
        throw new Error('No targets available');
      }
      
      // Generate forecasts for all targets with error handling per target
      // Filter out targets without latest_actual data
      const validTargets = dashboardData.targets.filter(target => 
        target.latest_actual?.value !== null && target.latest_actual?.value !== undefined
      );
      
      if (validTargets.length === 0) {
        throw new Error('No targets with valid data available for forecasting');
      }
      
      const promises = validTargets.map(async (target) => {
        try {
          return await macroForecastAPI.generateForecast({
            target_id: target.target_id,
            horizons: ['1M', '3M', '6M', '1Y'],
            method: 'KALMAN',
            include_shock: true,
            include_scenarios: true
          });
        } catch (err) {
          // Log error but don't fail entire batch
          console.error(`Failed to generate forecast for target ${target.target_id} (${target.indicator_id}):`, err);
          return {
            error: true,
            target_id: target.target_id,
            indicator_id: target.indicator_id,
            message: err.response?.data?.detail || err.message || 'Unknown error'
          };
        }
      });
      
      return await Promise.allSettled(promises);
    },
    onSuccess: (results) => {
      // Check for errors
      const errors = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && r.value?.error));
      if (errors.length > 0) {
        console.warn(`${errors.length} forecast(s) failed:`, errors);
      }
      
      // Invalidate and refetch dashboard data after generating
      queryClient.invalidateQueries({ queryKey: ['macroForecastDashboard'] });
      refetch();
    },
    onError: (err) => {
      console.error('Failed to generate forecasts:', err);
    }
  });

  const handleGenerateAll = async () => {
    setGenerating(true);
    setLocalError(null);
    try {
      const results = await generateMutation.mutateAsync();
      
      // Check for errors in results
      const errors = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && r.value?.error));
      if (errors.length > 0) {
        const errorMessages = errors.map(e => {
          if (e.status === 'rejected') {
            const detail = e.reason?.response?.data?.detail || e.reason?.message || 'Unknown error';
            // Extract indicator ID from error message if available
            const indicatorMatch = detail.match(/for target \d+ \((.+?)\)/);
            const indicatorId = indicatorMatch ? indicatorMatch[1] : 'Unknown';
            return `${indicatorId}: ${detail}`;
          } else if (e.value?.error) {
            return `${e.value.indicator_id}: ${e.value.message}`;
          }
          return 'Unknown error';
        });
        
        // Format error message more user-friendly
        const errorText = errorMessages.length === 1 
          ? errorMessages[0]
          : `${errorMessages.length} forecasts không thể tạo:\n${errorMessages.map((msg, idx) => `${idx + 1}. ${msg}`).join('\n')}`;
        
        setLocalError(errorText);
      }
    } catch (err) {
      console.error('Failed to generate forecasts:', err);
      setLocalError(err.response?.data?.detail || err.message || 'Failed to generate forecasts');
    } finally {
      setGenerating(false);
    }
  };

  const getTrendIcon = (value) => {
    if (!value) return <TrendingFlatIcon sx={{ fontSize: 32, color: 'text.secondary' }} />;
    if (value > 0) return <TrendingUpIcon sx={{ fontSize: 32, color: 'success.main' }} />;
    if (value < 0) return <TrendingDownIcon sx={{ fontSize: 32, color: 'error.main' }} />;
    return <TrendingFlatIcon sx={{ fontSize: 32, color: 'text.secondary' }} />;
  };

  const getScenarioColor = (scenario) => {
    switch (scenario) {
      case 'BASE': return 'primary';
      case 'UP': return 'success';
      case 'DOWN': return 'error';
      default: return 'default';
    }
  };

  const getIndicatorCategory = (indicatorId) => {
    // Explicit mapping table - easy to maintain and update
    const INDICATOR_CATEGORIES = {
      // Inflation indicators
      'CPI_YOY': 'inflation',
      'CPI_MOM': 'inflation',
      'INFLATION': 'inflation',
      'BRENT': 'inflation',
      'OIL_PRICE': 'inflation',
      
      // Credit indicators
      'CREDIT_YTD': 'credit',
      'CREDIT_YOY': 'credit',
      'CREDIT_GROWTH': 'credit',
      'LOAN_GROWTH': 'credit',
      'M2_GROWTH': 'credit',
      
      // Growth indicators
      'GDP_GROWTH': 'growth',
      'GDP_YOY': 'growth',
      'GDP_QOQ': 'growth',
      'INDUSTRIAL_PRODUCTION': 'growth',
      'RETAIL_SALES': 'growth',
      'EXPORT': 'growth',
      'IMPORT': 'growth',
      
      // Default to macro for unknown indicators
    };
    
    const category = INDICATOR_CATEGORIES[indicatorId] || 'macro';
    
    // Category styling configuration
    const CATEGORY_STYLES = {
      inflation: {
        color: '#ff9800',
        bgColor: '#fff3e0',
        icon: <LocalAtmIcon sx={{ fontSize: 14 }} />,
        label: i18n.language === 'vi' ? 'Lạm phát' : 'Inflation'
      },
      credit: {
        color: '#2196f3',
        bgColor: '#e3f2fd',
        icon: <AccountBalanceIcon sx={{ fontSize: 14 }} />,
        label: i18n.language === 'vi' ? 'Tín dụng' : 'Credit'
      },
      growth: {
        color: '#4caf50',
        bgColor: '#e8f5e9',
        icon: <ShowChartIcon sx={{ fontSize: 14 }} />,
        label: i18n.language === 'vi' ? 'Tăng trưởng' : 'Growth'
      },
      macro: {
        color: '#9c27b0',
        bgColor: '#f3e5f5',
        icon: <SpeedIcon sx={{ fontSize: 14 }} />,
        label: i18n.language === 'vi' ? 'Vĩ mô' : 'Macro'
      }
    };
    
    return {
      category,
      ...CATEGORY_STYLES[category]
    };
  };

  const getIndicatorDisplayName = (target) => {
    // Use API-provided target_name with i18n support
    const currentLang = i18n.language || 'vi';
    if (target?.target_name) {
      return target.target_name[currentLang] || target.target_name.en || target.indicator_id;
    }
    // Fallback to indicator_id if target_name is not available
    return target?.indicator_id || 'N/A';
  };

  const toggleExpanded = (targetId) => {
    setExpandedTargets(prev => ({
      ...prev,
      [targetId]: !prev[targetId]
    }));
  };

  const formatValue = (value, unit = '%', indicatorId = '') => {
    if (value === null || value === undefined) return 'N/A';
    
    // Special handling for index values vs percentage changes
    // Index values (base 100) should NOT have % symbol even if unit says %
    // Only actual percentage changes (YOY, MOM, GROWTH, YTD) should have %
    
    const isPercentageChange = indicatorId && (
      indicatorId.includes('YOY') || 
      indicatorId.includes('MOM') || 
      indicatorId.includes('GROWTH') ||
      indicatorId.includes('YTD')
    );
    
    // If value is > 50 and it's NOT a percentage change indicator, it's likely an index value
    // Examples: CPI Index (132), USDVND (25000), DXY (103), VIX (15), Brent (80)
    if (!isPercentageChange && value > 50) {
      // Remove % symbol for index values
      return value.toFixed(2);
    }
    
    return `${value.toFixed(2)}${unit}`;
  };

  if (loading) {
    return <MacroForecastSkeleton />;
  }

  // Show error from query
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }>
          {error?.message || 'Failed to load macro forecast dashboard'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* ═══════════════════════════════════════════════════════
          HEADER — Consistent with other pages
          ═══════════════════════════════════════════════════════ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        {/* Left: Icon + Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)',
          }}>
            <ShowChartIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
              {t('macroForecast.dashboard.title')}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('macroForecast.dashboard.subtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Right: Controls */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={selectedHorizon}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                setSelectedHorizon(newValue);
              }
            }}
            size="small"
            sx={{
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              overflow: 'hidden',
              '& .MuiToggleButton-root': {
                px: 1.5,
                py: 0.5,
                fontWeight: 600,
                fontSize: 13,
                border: 'none',
                color: '#64748b',
                '&.Mui-selected': {
                  bgcolor: '#8b5cf6',
                  color: 'white',
                  '&:hover': { bgcolor: '#7c3aed' },
                },
                '&:hover': { bgcolor: '#f8fafc' },
              },
            }}
          >
            <ToggleButton value="1M">{i18n.language === 'vi' ? '1 Tháng' : '1M'}</ToggleButton>
            <ToggleButton value="3M">{i18n.language === 'vi' ? '3 Tháng' : '3M'}</ToggleButton>
            <ToggleButton value="6M">{i18n.language === 'vi' ? '6 Tháng' : '6M'}</ToggleButton>
            <ToggleButton value="1Y">{i18n.language === 'vi' ? '1 Năm' : '1Y'}</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => refetch()}
            disabled={refreshing || generating}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              color: '#64748b', borderColor: '#e2e8f0',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {refreshing ? (i18n.language === 'vi' ? 'Đang làm mới...' : 'Refreshing...') : t('macroForecast.dashboard.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleGenerateAll}
            disabled={generating || refreshing}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {generating ? t('macroForecast.dashboard.generating') : t('macroForecast.dashboard.generateAll')}
          </Button>
        </Box>
      </Box>

      {/* Error Alert for Generate */}
      {localError && (
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="warning" 
            onClose={() => setLocalError(null)}
            action={
              <Button color="inherit" size="small" onClick={() => setLocalError(null)}>
                {i18n.language === 'vi' ? 'Đóng' : 'Dismiss'}
              </Button>
            }
            sx={{
              '& .MuiAlert-message': {
                whiteSpace: 'pre-line' // Allow line breaks in error message
              }
            }}
          >
            <Typography variant="body2" component="div">
              {localError.includes('Insufficient historical data') || localError.includes('không đủ dữ liệu')
                ? (i18n.language === 'vi' 
                  ? '⚠️ Không đủ dữ liệu lịch sử để tạo forecast. Một số indicators cần ít nhất 30 data points.\n\n' + localError
                  : '⚠️ Insufficient historical data to generate forecast. Some indicators require at least 30 data points.\n\n' + localError)
                : localError}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Macro Outlook Summary - Big Picture */}
      <MacroOutlookSummary 
        dashboardData={dashboardData} 
        selectedHorizon={selectedHorizon}
      />

      {/* Simple Forecast Cards - Easy to understand */}
      <Box sx={{ mt: 3 }}>
        {dashboardData?.targets?.map((target) => (
          <SimpleForecastCard 
            key={target.target_id} 
            target={target} 
            selectedHorizon={selectedHorizon}
          />
        ))}
      </Box>

      {/* Old Dashboard Cards - Hidden for now */}
      <Box sx={{ display: 'none', flexDirection: 'column', gap: 2.5 }}>
        {dashboardData?.targets?.map((target) => (
          <Card 
            key={target.target_id}
            elevation={0}
            sx={{ 
              width: '100%',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transform: 'translateY(-2px)'
              }
            }}
          >
              <CardContent sx={{ p: 0 }}>
                {/* Target Header with gradient accent */}
                <Box 
                  sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    height: '4px',
                    width: '100%'
                  }} 
                />
                
                <Box sx={{ p: 3, pb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                        <Typography 
                          variant="h5" 
                          fontWeight="700" 
                          sx={{ 
                            letterSpacing: '-0.5px',
                            fontSize: '1.35rem',
                            color: 'grey.900'
                          }}
                        >
                          {target.target_name?.[i18n.language] || target.target_name?.vi || target.target_name?.en || 'N/A'}
                        </Typography>
                        
                        {/* Status Label - Professional and neutral */}
                        <Box
                          sx={{
                            px: 1.25,
                            py: 0.4,
                            borderRadius: '12px',
                            backgroundColor: target.has_forecasts 
                              ? 'rgba(76, 175, 80, 0.1)' 
                              : 'rgba(158, 158, 158, 0.1)',
                            border: '1px solid',
                            borderColor: target.has_forecasts 
                              ? 'rgba(76, 175, 80, 0.25)' 
                              : 'rgba(158, 158, 158, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              color: target.has_forecasts ? '#2e7d32' : '#757575',
                              letterSpacing: '0.3px'
                            }}
                          >
                            {target.has_forecasts 
                              ? `✓ ${t('macroForecast.dashboard.forecasted')}`
                              : `○ ${t('macroForecast.dashboard.processing')}`
                            }
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          fontSize: '0.875rem',
                          lineHeight: 1.6,
                          color: 'grey.600'
                        }}
                      >
                        {target.description?.[i18n.language] || target.description?.vi || target.description?.en || ''}
                      </Typography>
                    </Box>
                    
                    {/* Professional Indicator Badge - Right side */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 3 }}>
                      {/* Category Badge */}
                      <Tooltip 
                        title={getIndicatorCategory(target.indicator_id).label}
                        placement="top"
                        arrow
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.25,
                            py: 0.6,
                            borderRadius: '10px',
                            backgroundColor: getIndicatorCategory(target.indicator_id).bgColor,
                            border: '1.5px solid',
                            borderColor: getIndicatorCategory(target.indicator_id).color,
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: getIndicatorCategory(target.indicator_id).color,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${getIndicatorCategory(target.indicator_id).color}40`,
                              '& .MuiSvgIcon-root': {
                                color: 'white'
                              },
                              '& .category-text': {
                                color: 'white'
                              }
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {React.cloneElement(getIndicatorCategory(target.indicator_id).icon, {
                              sx: { 
                                fontSize: 15, 
                                color: getIndicatorCategory(target.indicator_id).color,
                                transition: 'color 0.2s ease'
                              }
                            })}
                          </Box>
                          <Typography
                            className="category-text"
                            sx={{
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              color: getIndicatorCategory(target.indicator_id).color,
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px',
                              transition: 'color 0.2s ease'
                            }}
                          >
                            {getIndicatorCategory(target.indicator_id).label}
                          </Typography>
                        </Box>
                      </Tooltip>
                      
                      {/* Indicator Code Badge */}
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.6,
                          borderRadius: '10px',
                          backgroundColor: 'grey.50',
                          border: '1.5px solid',
                          borderColor: 'grey.300'
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: 'grey.700',
                            fontFamily: 'monospace',
                            letterSpacing: '0.5px'
                          }}
                        >
                          {getIndicatorDisplayName(target)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Latest Actual Value */}
                <Box sx={{ 
                  mx: 3,
                  mb: 3, 
                  p: 2.5, 
                  bgcolor: 'linear-gradient(135deg, #f5f7fa 0%, #f8f9fb 100%)',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    fontWeight="700" 
                    sx={{ 
                      mb: 2, 
                      display: 'block', 
                      textTransform: 'uppercase', 
                      fontSize: '0.68rem',
                      letterSpacing: '0.8px',
                      color: 'grey.600'
                    }}
                  >
                    {t('macroForecast.dashboard.latestActual', 'GIÁ TRỊ THỰC TẾ')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'white',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                        border: '1px solid',
                        borderColor: 'grey.100'
                      }}
                    >
                      {getTrendIcon(target.latest_actual?.value)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="h3" 
                        fontWeight="800" 
                        sx={{ 
                          lineHeight: 1, 
                          mb: 0.75,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontSize: '2.25rem'
                        }}
                      >
                        {formatValue(target.latest_actual?.value, target.unit, target.indicator_id)}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.8rem',
                          color: 'grey.600',
                          fontWeight: 500
                        }}
                      >
                        {target.latest_actual?.date ? new Date(target.latest_actual.date).toLocaleDateString('vi-VN', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        }) : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Forecasts */}
                {target.has_forecasts ? (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="600">
                          {t('macroForecast.dashboard.forecasts')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {target.as_of_time ? new Date(target.as_of_time).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        onClick={() => toggleExpanded(target.target_id)}
                        endIcon={
                          <ExpandMoreIcon 
                            sx={{ 
                              transform: expandedTargets[target.target_id] ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s',
                              fontSize: '1rem'
                            }} 
                          />
                        }
                        sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                      >
                        {expandedTargets[target.target_id] ? t('macroForecast.dashboard.collapse') : t('macroForecast.dashboard.expand')}
                      </Button>
                    </Box>
                  
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {expandedTargets[target.target_id] ? (
                        // Expanded: Show all horizons
                        Object.entries(target.forecasts || {}).map(([horizon, scenarios]) => (
                          <Box 
                            key={horizon} 
                            sx={{ 
                              p: 1.5, 
                              bgcolor: 'background.paper',
                              borderRadius: 1.5,
                              border: '1px solid',
                              borderColor: 'grey.200'
                            }}
                          >
                            <Typography variant="caption" fontWeight="700" sx={{ mb: 1, display: 'block', color: 'text.primary', fontSize: '0.75rem' }}>
                              {horizon}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {Object.entries(scenarios).map(([scenario, data]) => (
                                <Box 
                                  key={scenario}
                                  sx={{ 
                                    flex: 1,
                                    p: 1.5, 
                                    borderRadius: 1, 
                                    bgcolor: `${getScenarioColor(scenario)}.50`,
                                    border: '1.5px solid',
                                    borderColor: `${getScenarioColor(scenario)}.main`,
                                    textAlign: 'center'
                                  }}
                                >
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      display: 'block', 
                                      mb: 0.5,
                                      fontWeight: 700,
                                      fontSize: '0.65rem',
                                      textTransform: 'uppercase',
                                      color: `${getScenarioColor(scenario)}.dark`
                                    }}
                                  >
                                    {t(`macroForecast.scenario.${scenario}`)}
                                  </Typography>
                                  <Typography 
                                    variant="h6" 
                                    fontWeight="700" 
                                    color={`${getScenarioColor(scenario)}.main`}
                                    sx={{ mb: 0.5 }}
                                  >
                                    {formatValue(data.mean, target.unit, target.indicator_id)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                    {data.lower && data.upper ? `${formatValue(data.lower, target.unit, target.indicator_id)} - ${formatValue(data.upper, target.unit, target.indicator_id)}` : ''}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        ))
                      ) : (
                        // Collapsed: Show summary
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {Object.entries(target.forecasts || {}).slice(0, 2).map(([horizon, scenarios]) => (
                            <Box key={horizon} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="caption" fontWeight="700" sx={{ display: 'block', mb: 0.75, fontSize: '0.7rem' }}>
                                {horizon}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                {Object.entries(scenarios).map(([scenario, data]) => (
                                  <Chip
                                    key={scenario}
                                    label={`${t(`macroForecast.scenario.${scenario}`)}: ${formatValue(data.mean, target.unit, target.indicator_id)}`}
                                    size="small"
                                    color={getScenarioColor(scenario)}
                                    sx={{ fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </>
                ) : (
                <Alert severity="info">
                  {t('macroForecast.dashboard.noForecasts')}
                </Alert>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Footer Info */}
      {dashboardData?.generated_at && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {t('macroForecast.dashboard.generatedAt')}: {new Date(dashboardData.generated_at).toLocaleString()}
            {refreshing && ' (Đang cập nhật...)'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DashboardMacroForecast;

